/* eslint-disable max-lines */
import * as React from 'react';
import { useSelector } from 'react-redux';
import { ArticleParagraph1, UIText1 } from '@cian/ui-kit';

import { IFAQCategory } from '../../../types/promoInfo/faq';
import { selectIsAccreditiveActive } from '../../../selectors/promoInfo';

interface IFAQOptions {
  isAbsolutBankEnabled: boolean;
}

/* istanbul ignore next */
export const useGetContent = ({ isAbsolutBankEnabled }: IFAQOptions): IFAQCategory[] => {
  const isAccreditiveActive = useSelector(selectIsAccreditiveActive);

  return React.useMemo(() => {
    return [
      {
        category: 'Про возможности сделки',
        questions: [
          {
            title: 'Могут ли покупатель и продавец самостоятельно провести сделку через сервис «Циан.Сделка»?',
            content: (
              <ArticleParagraph1>
                Нет, воспользоваться сервисом «Циан.Сделка» без профессионального участника рынка невозможно.
              </ArticleParagraph1>
            ),
          },
          {
            title: 'Сколько занимает электронная регистрация права собственности?',
            content: (
              <ArticleParagraph1>
                Электронная регистрация перехода права собственности через сервис «Циан.Сделка» в среднем занимает 5
                рабочих дней: ~2 дня — доставка электронных подписей представителем удостоверяющего центра и подписание
                документов; ~3 дня — регистрация перехода права собственности в Росреестре. При этом по закону{' '}
                <a
                  href="https://rosreestr.gov.ru/press/archive/publications/sroki-registratsii-ischislyayutsya-v-rabochikh-dnyakh/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  регистрация в Росреестре может идти до 7 рабочих дней включительно
                </a>
                .
              </ArticleParagraph1>
            ),
          },
          {
            title: 'Можно ли через Циан.Сделку оформить покупку / продажу дома с земельным участком?',
            content: (
              <ArticleParagraph1>
                На данный момент через Циан.Сделку можно зарегистрировать дом и земельный участок, на котором он стоит,
                подав 2 отдельные заявки на сделку подряд. Расчёт возможен любым другим способом вне нашего сервиса
                (банковская ячейка, наличные, аккредитивы в офисах банков, СБР других сервисов и т.д.).
              </ArticleParagraph1>
            ),
          },
          {
            title: 'Можно ли через Циан.Сделку оформить переуступку прав требований  квартиры в новостройке?',
            content: (
              <ArticleParagraph1>
                На данный момент через Циан.Сделку можно зарегистрировать переуступку прав требований квартиры в
                новостройке. Для оформления может потребоваться согласие от застройщика (в том случае если в ДДУ
                прописано, что обмен в электронном виде). Переуступку прав требований можно оформить только между
                физическими лицами.
              </ArticleParagraph1>
            ),
          },
          {
            title: 'Вы проводите юридическую проверку недвижимости перед электронной регистрацией?',
            content: (
              <ArticleParagraph1>
                Нет, но вы можете воспользоваться нашим сервисом{' '}
                <a href="https://www.cian.ru/legal-check/" rel="noopener noreferrer" target="_blank">
                  «Проверка недвижимости»
                </a>
                . Опытные юристы изучат объект и продавца, расскажут о возможных рисках и помогут их избежать.
              </ArticleParagraph1>
            ),
          },
          {
            title: 'В каких городах вы проводите электронную регистрацию?',
            content: (
              <ArticleParagraph1>
                Объект недвижимости, с которым проводится сделка, может находиться почти в любой точке РФ. Встречи
                участников сделки с представителем удостоверяющего центра проводятся в большинстве регионов РФ, с полным
                списком городов вы можете ознакомиться{' '}
                <a href="https://hc.cian.ru/hc/ru/articles/4408340109204" rel="noopener noreferrer" target="_blank">
                  по ссылке
                </a>
                .
              </ArticleParagraph1>
            ),
          },
          {
            title: 'Какие еще есть ограничения на проведение электронной регистрации с помощью сервиса «Циан.Сделка»?',
            content: (
              <>
                <ArticleParagraph1>
                  Мы не регистрируем объекты, находящиеся под обременением, в том числе в залоге у банка. Также не
                  регистрируем цепочки связанных (альтернативных) сделок в рамках одной услуги, но вы можете
                  зарегистрировать несколько последовательных сделок по отдельности.
                </ArticleParagraph1>
                <ArticleParagraph1>Типы объектов недвижимости, с которыми мы проводим сделки:</ArticleParagraph1>
                <ul>
                  <li>
                    <UIText1>квартира на вторичном рынке;</UIText1>
                  </li>
                  <li>
                    <UIText1>квартира в новостройке по переуступке;</UIText1>
                  </li>
                  <li>
                    <UIText1>апартаменты;</UIText1>
                  </li>
                  <li>
                    <UIText1>комната на вторичном рынке;</UIText1>
                  </li>
                  <li>
                    <UIText1>гараж;</UIText1>
                  </li>
                  <li>
                    <UIText1>машино-место;</UIText1>
                  </li>
                  <li>
                    <UIText1>жилой дом;</UIText1>
                  </li>
                  <li>
                    <UIText1>
                      земельный участок, предназначенный для ведения личного подсобного, дачного хозяйства,
                      огородничества, садоводства, индивидуального гаражного или индивидуального жилищного
                      строительства;
                    </UIText1>
                  </li>
                  <li>
                    <UIText1>
                      коммерческая недвижимость (торговая площадь, склад, помещение свободного назначения, здание);
                    </UIText1>
                  </li>
                  <li>
                    <UIText1>постройки: садовый дом, баня, сарай.</UIText1>
                  </li>
                </ul>
                <ArticleParagraph1>При этом объект недвижимости:</ArticleParagraph1>
                <ul>
                  <li>
                    <UIText1>не находится в залоге;</UIText1>
                  </li>
                  <li>
                    <UIText1>не приобретён за счёт средств материнского (семейного) капитала;</UIText1>
                  </li>
                  <li>
                    <UIText1>право собственности на него возникло после 31.01.1998 г.</UIText1>
                  </li>
                </ul>
                <ArticleParagraph1>
                  Услуга СБР не оказывается по Сделкам, где Объектами в рамках одной Сделки выступают земельный участок
                  и гараж/жилой дом/хозяйственные постройки. Участником сделки может выступать дееспособный гражданин
                  Российской Федерации, достигший 18 лет. У всех участников обязательно должны быть смартфоны, на
                  которые можно установить специальное приложение для электронной подписи документов. В смартфоне должна
                  быть установлена сим-карта с номером мобильного телефона, которой был указан в заявке на регистрацию.
                </ArticleParagraph1>
                <ArticleParagraph1>
                  В сделке могут принимать участие не более 10 покупателей и 10 продавцов. Без участия законных
                  представителей и\или представителей участников сделки, действующих по доверенности.
                </ArticleParagraph1>
                <ArticleParagraph1>
                  При этом у собственника отсутствует запрет на проведение сделки в электронном виде, а покупатель
                  должен оплачивать объект недвижимости без использования заемных денежных средств (ипотеки) или средств
                  материнского (семейного) капитала.
                </ArticleParagraph1>
                <ArticleParagraph1>
                  Оплатить услугу «Циан.Сделка» можно только онлайн. Ссылка на оплату отправляется покупателю после
                  оформления заявки на сделку.
                </ArticleParagraph1>
              </>
            ),
          },
          {
            title: 'Можно ли провести Циан.Cделку, если участник находится за пределами РФ?',
            content: (
              <>
                <ArticleParagraph1>Можно, но есть ряд ограничений:</ArticleParagraph1>
                <ul>
                  <li>
                    <UIText1>
                      у участника сделки должен быть при себе заграничный паспорт нового образца (на 10 лет) и смартфон
                      с NFC-модулем;
                    </UIText1>
                  </li>
                  <li>
                    <UIText1>
                      у участника сделки должна быть в наличии активная сим-карта с российским номером (на неё придет
                      смс с кодом авторизации);
                    </UIText1>
                  </li>
                  <li>
                    <UIText1>
                      у участника сделки должно быть цветное изображение паспорта гражданина РФ (фото/скан);
                    </UIText1>
                  </li>
                  <li>
                    <UIText1>
                      подписание документов будет производиться с помощью приложения «Госключ». Чтобы им воспользоваться
                      у клиента должна быть подтверждённая учётная запись на портале Госуслуг.
                    </UIText1>
                  </li>
                </ul>
              </>
            ),
          },
          {
            title: 'Сколько участников могут одновременно принимать участие в сделке через сервис Циан.Сделка?',
            content: (
              <ArticleParagraph1>
                В сделке пока могут принимать участие не более 10 покупателей и не более 10 продавцов.
              </ArticleParagraph1>
            ),
          },
          {
            title: 'Дарение возможно подать на регистрацию через Циан.Сделку?',
            content: <ArticleParagraph1>Сделки по дарению пока не проводим.</ArticleParagraph1>,
          },
          {
            title:
              'Можно ли зарегистрировать цепочки из сделок с возможностью их одновременного рассмотрения одним сотрудником Росреестра?',
            content: (
              <ArticleParagraph1>
                Нет, т.к. такое действие незаконно, но мы можем зарегистрировать отдельно все ваши сделки в цепочке.
              </ArticleParagraph1>
            ),
          },
        ],
      },
      {
        category: 'Про безопасность',
        questions: [
          {
            title: 'Электронные подписи безопасны?',
            content: (
              <>
                <ArticleParagraph1>
                  Короткий ответ: да. Такие подписи создаются с помощью криптографических методов защиты, подделать их
                  почти невозможно — в отличии от обычных подписей ручкой.
                </ArticleParagraph1>
                <ArticleParagraph1>
                  Для сделки используются усиленные квалифицированные электронные подписи — их выдают только
                  удостоверяющие центры с аккредитацией Минцифры РФ.
                </ArticleParagraph1>
              </>
            ),
          },
          {
            title: 'У приложения для подписания документов низкий рейтинг. Оно безопасно?',
            content: (
              <ArticleParagraph1>
                Подписание документов через приложение безопасно. Низкий рейтинг обусловлен циклом использования
                приложения. Пользователь устанавливает его, подписывает документы и сразу удаляет. Магазины приложений
                Play Market и AppStore автоматически снижают за это рейтинги.
              </ArticleParagraph1>
            ),
          },
          {
            title: 'Как можно проводить сделки, не видя очно продавца и покупателя? Это небезопасно!',
            content: (
              <ArticleParagraph1>
                Подписать документы электронными подписями смогут только участники сделки, которым они принадлежат.
                Подписание будет происходить в специальном приложении, куда мы вышлем документы, необходимые для
                проведения регистрации. Документы в Росреестр отправим, когда все участники сделки их подпишут.
              </ArticleParagraph1>
            ),
          },
          {
            title: 'Зачем нужна видеовстреча с представителем банка?',
            content: isAbsolutBankEnabled ? (
              <ArticleParagraph1>
                На онлайн-встрече в Zoom представитель одного из банков-партнёров — Точка или Абсолют — проверяет
                паспорта участников сделки и задаёт им вопросы по значимым тезисам ДКП. Это нужно для выдачи уведомления
                об участии банка в сделке — без него она будет считаться незаконной.
              </ArticleParagraph1>
            ) : (
              <ArticleParagraph1>
                На онлайн-встрече в Zoom представитель банка Точка идентифицирует личности и проверяет паспорта
                участников и задаёт вопросы по значимым тезисам ДКП. Это нужно для выдачи «Уведомления об участии банка»
                по сделке в соответствии с № 218-ФЗ. Без этого документа провести сделку не получится.
              </ArticleParagraph1>
            ),
          },
        ],
      },
      {
        category: 'Про взаиморасчёты',
        questions: [
          {
            title: 'Можно ли использовать реквизиты родственников в сервисе безопасных расчётов от Циан?',
            content: (
              <ArticleParagraph1>
                Нет. Счёт, на который Циан переведёт деньги за объект недвижимости, должен быть открыть строго на одного
                из продавцов. Циан не будет переводить деньги на реквизиты супруга продавца, родственника или прочих
                третьих лиц, не являющихся сторонами сделки по договору купли-продажи.
              </ArticleParagraph1>
            ),
          },
          ...(isAccreditiveActive
            ? [
                {
                  title: 'Как произвести расчёт через электронный аккредитив от СберБанка',
                  content: (
                    <>
                      <ArticleParagraph1>
                        Риелтор, который ведёт сделку со стороны покупателя или продавца, подаёт заявку на сделку в
                        сервисе Циан.Сделка в своём личном кабинете. Циан, в свою очередь, передаёт данные для открытия
                        аккредитива по защищённому каналу в СберБанк.
                      </ArticleParagraph1>
                      <ArticleParagraph1>
                        Покупатель получает от СберБанка смс со ссылкой и за пару кликов подписывает заявку на открытие
                        аккредитива в мобильном приложении СберБанк Онлайн. Чтобы воспользоваться услугой, покупателю
                        обязательно нужно быть клиентом СберБанка и пользователем мобильного приложения СберБанк Онлайн.
                      </ArticleParagraph1>
                      <ArticleParagraph1>
                        Покупатель оплачивает услугу в размере 2 000 ₽ в момент открытия аккредитива в мобильном
                        приложении СберБанк Онлайн. Перевести деньги за недвижимость на счёт аккредитива можно бесплатно
                        со сберегательного счёта. После перевода средств на счёт аккредитива продавцу придёт смс от
                        СберБанка об открытии аккредитива.
                      </ArticleParagraph1>
                      <ArticleParagraph1>
                        После выполнения условий аккредитива СберБанк переведёт деньги продавцу в течение одного
                        рабочего дня.
                      </ArticleParagraph1>
                      <ArticleParagraph1>
                        Платёжные документы, подтверждающие перевод денежных средств продавцу, можно скачать в
                        приложении СберБанк Онлайн.
                      </ArticleParagraph1>
                      <ArticleParagraph1>
                        Если сделка не состоится, закрыть аккредитив можно в любое время.
                      </ArticleParagraph1>
                      <ArticleParagraph1>
                        Покупатель может подать заявление на отзыв аккредитива в мобильном приложении СберБанк Онлайн
                        или в любом офисе СберБанка.
                      </ArticleParagraph1>
                      <ArticleParagraph1>
                        Денежные средства будут возвращены покупателю когда продавец подтвердит свое согласие на отзыв в
                        мобильном приложении СберБанк Онлайн или в любом офисе СберБанка.
                      </ArticleParagraph1>
                    </>
                  ),
                },
              ]
            : []),
        ],
      },
      {
        category: 'Про электронную подпись',
        questions: [
          {
            title: 'Электронные подписи безопасны?',
            content: (
              <>
                <ArticleParagraph1>
                  Короткий ответ: да. Такие подписи создаются с помощью криптографических методов защиты, подделать их
                  почти невозможно — в отличии от обычных подписей ручкой.
                </ArticleParagraph1>
                <ArticleParagraph1>
                  Для сделки используются усиленные квалифицированные электронные подписи — их выдают только
                  удостоверяющие центры с аккредитацией Минцифры РФ.
                </ArticleParagraph1>
              </>
            ),
          },
          {
            title: 'Как можно активировать электронную подпись по загранпаспорту?',
            content: (
              <ArticleParagraph1>
                В загранпаспорте нового образца есть биометрический чип. Приложение «Госключ» сканирует чип с помощью
                технологии NFC в смартфоне, и удостоверив личность, выпускает квалифицированную электронную подпись.
                Также для выпуска такой подписи в приложении «Госключ» нужна подтвержденная учётная запись на портале
                Госуслуг.
              </ArticleParagraph1>
            ),
          },
          {
            title: 'Можно ли  заранее ознакомиться с документами, направляемыми на подписание в приложение?',
            content: (
              <ArticleParagraph1>
                Да, одно из преимуществ нашего сервиса в том, что участники сделки могут спокойно и не торопясь
                ознакомиться с документами, которые направляются им на подписание в мобильное приложение. Если нужно
                заменить документы, напишите об этом персональному менеджеру: он направит вам на подписание
                скорректированные документы.
              </ArticleParagraph1>
            ),
          },
        ],
      },
      {
        category: 'Другое',
        questions: [
          {
            title: 'Моим клиентам нужны документы с синими печатями',
            content: (
              <ArticleParagraph1>
                Документы, подтверждающие регистрацию перехода права собственности, Росреестр отправляет в электронном
                виде. Как правило, нет необходимости в бумажной версии документов. Но вы всегда можете получить
                отождествление у нотариуса — это просто, быстро и недорого.
              </ArticleParagraph1>
            ),
          },
          {
            title: 'У меня есть вопросы по документам, статусам и срокам сделки. Кому мне их задать?',
            content: (
              <>
                <ArticleParagraph1>
                  Каждому клиенту, который завёл заявку на сделку, мы назначаем персонального менеджера. Он
                  проконсультирует по любому вопросу, связанному со сделкой, объяснит все шаги и нюансы, поможет с
                  документами. В нашем сервисе сделку ведёт один персональный менеджер от начала и до конца: мы не будем
                  перебрасывать вас от одного сотрудника к другому.
                </ArticleParagraph1>
                <ArticleParagraph1>
                  Если вы ещё не завели заявку на сделку, то наши специалисты с радостью вас проконсультируют по любому
                  вопросу в чате или по электронной почте support-deal@cian.ru с 7 до 18 по московскому времени.
                </ArticleParagraph1>
              </>
            ),
          },
        ],
      },
    ];
  }, [isAccreditiveActive]);
};
